import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AdjustmentsHorizontalIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import DashboardTile from '../../components/DashboardTile';
import CountPanel from '../../components/CountPanel';
import { getInvoiceBuckets } from '../../services/pendingEdiInvoices.service';
import ModalHeader from '../../components/ui/ModalHeader';
import { Drawer } from '../../components/ui/Drawer';
import Toggle from '../../components/Toggle';
import { useModalDrag } from '../../hooks/useModalDrag';
import useDrawerResize from '../../hooks/useDrawerResize';
import { useToast } from '../../contexts/ToastContext';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import userSettingsService, { PanelLayoutSource } from '../../services/userSettings.service';
import { usePanelGrid } from '../../components/panelGrid/usePanelGrid';
import { usePanelLayoutPersistence } from '../../components/panelGrid/usePanelLayoutPersistence';
import {
  PanelLayoutItem,
  PanelLayouts,
  TILE_GRID_OPTIONS,
  resolvePanelItems,
} from '../../components/panelGrid/panelGridTypes';
import { isDefaultPanelArrangement } from '../../components/panelGrid/panelGeometry';
import {
  applyCopiedPanelLayouts,
  latestRestorableBatch,
  restorePanelBatch,
  trashPanelLayout,
} from '../../components/panelGrid/panelLayoutTrash';
import {
  ACCT_COUNT_PANELS,
  ACCT_DASHBOARD_TYPE,
  ACCT_PANEL_DEFS,
  ACCT_PANELS_SETTING_KEY,
  ACCT_WORKSPACE_ID,
  getDefaultPanelLayouts,
} from './accountingPanels';
import { drillTargetFor } from './accountingDrill';
import BucketFilterModal from '../../components/BucketFilterModal';
import type { FilterCondition } from '../../components/FilterConditionRow';
import { useOptionalFieldDefinitions } from '../../contexts/FieldDefinitionsContext';

/** Stable empty array: a fresh [] each render would change useDrillData's conditions key
 *  and refetch the drill on every parent render. */
const EMPTY_CONDITIONS: FilterCondition[] = [];

/** The Pending Invoicing > EDI row: counted and opened by the EDI Pending Invoices page,
 *  not by the dashboard cache. */
const EDI_ROW_KEY = 'acct-pinv-edi';
const EDI_PAGE_PATH = '/accounting/pending-edi-invoices';
/** V1's Total block is the root of its accounting tree — every order in scope
 *  (dashboard_bucket_cms.pl:855, `accounting_map{'Bill From System'}='1'`). */
const TOTAL_BUCKET_KEY = 'acct-total-bill-from-system';

/**
 * V1's Total block (dashboard_accounting.pl:378): the root of the accounting tree, so the
 * count is every order in scope, CMS + Sage + DO NOT BILL together. Deliberately not
 * clickable — the bucket holds ~34k members and the drill endpoint has no row cap.
 */
const TotalPanel: React.FC<{ count?: number; amount?: number; loading: boolean }> = ({ count, amount, loading }) => (
  <div className="flex items-center justify-between gap-2 bg-slate-700 px-2 py-1 text-sm font-semibold text-white">
    <span>Bill From System</span>
    <span className="tabular-nums">
      {typeof count !== 'number' ? (loading ? '…' : '—') : count.toLocaleString()}
    </span>
    <span className="w-24 shrink-0 text-right tabular-nums">
      {typeof amount !== 'number' ? (loading ? '…' : '') : `$${Math.round(amount).toLocaleString()}`}
    </span>
  </div>
);
import { useDrillCardLayout } from './useDrillCardLayout';
import { useAcctSummary } from './useAcctSummary';
import AccountingDrillCard from './AccountingDrillCard';
import { ACCRUAL_TITLE, DriverPayrollAccrualForm, DriverPayrollAccrualResults } from './DriverPayrollAccrual';
import { INVOICE_SEARCH_TITLE, InvoiceSearchForm, InvoiceSearchResults } from './InvoiceSearchCard';
import SagePanel, { type SageAction, type SageActionKey } from './SagePanel';
import SageUploadCard from './SageUploadCard';
import SageExportCard from './SageExportCard';
import SageConfirmCard from './SageConfirmCard';
import type { SageFamily } from '../../services/sage.service';
import type { InvoiceListParams } from '../../services/invoiceList.service';
import type { AccrualQuery } from '../../services/driverPayrollAccrual.service';
import './AccountingDashboards.module.css';

// =============================================================================
// Accounting Dashboards — GWI360 port of V1's Accounting Dashboard (phase 1: shell).
// =============================================================================
// One workspace, a 24-col GridStack of tiles (count panels + V1's action blocks, all
// placeholders until their card phase). A clicked count row opens its drill the way LC
// Dashboards does — never below the tiles: in page view the tiles hide and the results take
// over the page behind a "Back to dashboard" bar; in drawer view the tiles stay and the
// results slide in from the right in a drawer that is resizable, snaps to the app nav, and
// persists its width like the PRO detail drawer. The per-user "Drawer view" toggle picks the
// mode, and selection is exclusive (one drill open at a time). Tile layout and the open drill
// persist per user; see docs/plans/2026-09-09-accounting-dashboards-shell-design.md.
// Unrelated to LC Dashboards' Accounting workspace — different numbers, different team.

const REFRESH_MS = 30_000;
/** user_settings key for the Drawer-view preference (`{ drawerView: boolean }`). */
export const ACCT_VIEW_SETTING_KEY = 'accounting_dashboard_view';
const MENU_ITEM = 'w-full flex items-center gap-2 px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary text-label disabled:opacity-50 disabled:cursor-not-allowed';

/** What each V1 action block does, shown in its placeholder until that card's phase lands. */
const ACTION_PLACEHOLDERS: Record<string, string[]> = {
  'acct-total': ['Grand total across every bucket on this page.'],
  'acct-invoice-search': ['Invoice # or Master Pro', 'Bill To', 'Invoice Date range', 'Search'],
};

const ComingSoon: React.FC<{ panelKey: string }> = ({ panelKey }) => (
  <div className="p-3 text-sm text-muted">
    <p className="font-medium text-label">Coming soon</p>
    <ul className="mt-1 list-disc pl-4 space-y-0.5">
      {(ACTION_PLACEHOLDERS[panelKey] ?? []).map((line) => <li key={line}>{line}</li>)}
    </ul>
  </div>
);

/** Shown beside the open drill's title while its target is still a placeholder. */
const NotWiredPill: React.FC = () => (
  <span
    className="rounded-full border border-slate-500 bg-slate-700 px-2 py-0.5 text-[11px] font-semibold text-white"
    title="This card's data source arrives in a later phase"
  >
    Data source not wired yet
  </span>
);

/** One Sage action -> the card that renders it into the shared results pane. */
const SageActionResults: React.FC<{ action: SageActionKey }> = ({ action }) => {
  if (action === 'confirm') return <SageConfirmCard />;
  if (action === 'upload-billtos') return <SageUploadCard kind="billtos" />;
  if (action === 'upload-vendors') return <SageUploadCard kind="vendors" />;
  return <SageExportCard family={action.replace('export-', '') as SageFamily} />;
};

const AccountingDashboards: React.FC = () => {
  const { showToast } = useToast();

  // ---- Custom filter, per bucket -------------------------------------------------
  // Ephemeral by design, exactly as on the LC board: component state, never persisted,
  // so it survives the 30s refresh and a drill reopen but is gone once you leave the
  // page. Held HERE rather than in the drill card because the card is keyed on rowKey
  // and remounts when you switch buckets — the filter should outlive that within a visit.
  //
  // The tile counts cannot follow it at all: they come from cache_acct_dashboard_buckets,
  // rebuilt every 5 minutes by Sync. So a filtered grid will not match the number above
  // it, and the grid's "Custom Filtered" badge is the only account of that.
  const [bucketFilters, setBucketFilters] = useState<Record<string, FilterCondition[]>>({});
  const [filterTarget, setFilterTarget] = useState<{ key: string; label: string } | null>(null);
  // Optional: display names only sharpen the badge's hover, and this page should not
  // take an auth-dependent provider as a hard dependency for a tooltip.
  const fieldDefinitions = useOptionalFieldDefinitions();

  const applyBucketFilter = useCallback((rowKey: string, conditions: FilterCondition[]) => {
    setBucketFilters((prev) => {
      const next = { ...prev };
      if (conditions.length) next[rowKey] = conditions;
      else delete next[rowKey];
      return next;
    });
    setFilterTarget(null);
  }, []);

  /** One readable line per condition for the badge's hover, with display names resolved —
   *  `o_origapptnumber` in a tooltip explains nothing about why a bucket is short. */
  const describeConditions = useCallback((conditions?: FilterCondition[]) => {
    if (!conditions?.length) return undefined;
    return conditions
      .map((c) => {
        const label = fieldDefinitions.find((f) => f.fieldname === c.field)?.displayname || c.field;
        return `• ${label} ${c.compare.toLowerCase()} ${c.values}`.trimEnd();
      })
      .join('\n');
  }, [fieldDefinitions]);

  // ---- Refresh: one page-wide tick every card shares ---------------------------
  const [refreshTick, setRefreshTick] = useState(0);
  useVisibilityAwareInterval(() => setRefreshTick((t) => t + 1), REFRESH_MS);
  // The tiles' numbers. "Last Updated" is the CACHE's rebuild stamp, not our fetch time —
  // the Sync job rebuilds every 5 minutes, so a 30s poll only picks up that stamp.
  const summary = useAcctSummary(refreshTick);

  // ---- Tile grid -----------------------------------------------------------------
  const panels = usePanelLayoutPersistence(
    ACCT_PANELS_SETTING_KEY,
    getDefaultPanelLayouts,
    () => showToast({ type: 'error', message: 'Could not save your tile layout — it will retry on your next change' }),
  );
  const { setPanelLayouts, panelLayoutsRef, panelTrashRef, applyTrashResult } = panels;
  // Design §5: a failed load shows the default and says so once; saving stays blocked
  // until a load succeeds (the hook enforces that).
  useEffect(() => {
    if (panels.loadFailed) {
      showToast({ type: 'error', message: 'Could not load your tile layout — showing the default until it loads' });
    }
  }, [panels.loadFailed, showToast]);
  const panelItems = useMemo(
    () => resolvePanelItems(
      ACCT_PANEL_DEFS,
      panels.panelLayouts[ACCT_WORKSPACE_ID],
      getDefaultPanelLayouts()[ACCT_WORKSPACE_ID],
    ),
    [panels.panelLayouts],
  );
  const onPanelItemsChange = useCallback((items: PanelLayoutItem[]) => {
    setPanelLayouts({ ...panelLayoutsRef.current, [ACCT_WORKSPACE_ID]: items });
  }, [setPanelLayouts, panelLayoutsRef]);
  const panelsReady = panels.loaded || panels.loadFailed;
  const tileGrid = usePanelGrid({
    items: panelItems,
    ready: panelsReady,
    options: TILE_GRID_OPTIONS,
    onItemsChange: onPanelItemsChange,
  });

  // First-load auto-fit, matching LC: a tile that has never been sized shows its
  // arbitrary default footprint until its content lands, so snap it once the counts
  // arrive and let the grid's own 'change' persist the snapped size. A tile the user
  // sized, or locked, is left alone — same rule LC uses.
  const autoFitted = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!tileGrid.gridReady || !panelsReady) return;
    if (summary.loading && !summary.syncedAt) return;   // nothing rendered to measure yet
    const saved = panelLayoutsRef.current[ACCT_WORKSPACE_ID] || [];
    ACCT_PANEL_DEFS.forEach((def) => {
      if (autoFitted.current.has(def.key)) return;
      const item = saved.find((i) => i.key === def.key);
      if (item?.locked) return;
      const neverSized = !item || (!item.collapsed && item.w === def.defaultW && item.h === def.defaultH);
      if (!neverSized) return;
      autoFitted.current.add(def.key);
      tileGrid.fitPanel(def.key);
    });
  }, [tileGrid, panelsReady, summary.loading, summary.syncedAt, panelLayoutsRef]);

  // ---- Drill selection (exclusive, like LC) -----------------------------------------
  const drill = useDrillCardLayout(
    ACCT_DASHBOARD_TYPE,
    () => showToast({ type: 'error', message: 'Could not save your drill cards — it will retry on your next change' }),
  );
  const { selectCard, hideCard } = drill;
  useEffect(() => {
    if (drill.loadFailed) {
      showToast({ type: 'error', message: 'Could not load your drill cards — starting empty until they load' });
    }
  }, [drill.loadFailed, showToast]);
  // The one open drill. A persisted card whose key the registry no longer knows is skipped
  // here, so it can never swap the page for an empty results box.
  const selectedCard = useMemo(
    () => drill.cards.find((c) => c.visible && drillTargetFor(c.key)) ?? null,
    [drill.cards],
  );
  const selectedTarget = selectedCard ? drillTargetFor(selectedCard.key) ?? null : null;
  const [accrualQuery, setAccrualQuery] = useState<AccrualQuery | null>(null);
  const [accrualOpen, setAccrualOpen] = useState(false);
  const [accrualRun, setAccrualRun] = useState(0);
  const [invoiceSearch, setInvoiceSearch] = useState<{ params: InvoiceListParams; run: number } | null>(null);
  const [sage, setSage] = useState<SageAction | null>(null);
  const invoiceSearchRun = useRef(0);
  const hasResults = !!invoiceSearch || accrualOpen || !!sage || !!selectedTarget;
  const resultsTitle = invoiceSearch ? INVOICE_SEARCH_TITLE : accrualOpen ? ACCRUAL_TITLE : sage ? sage.title : selectedTarget?.title ?? '';
  const notWired = !invoiceSearch && !accrualOpen && !sage && selectedTarget?.descriptor.kind === 'placeholder';
  const closeResults = () => {
    setAccrualOpen(false);
    setInvoiceSearch(null);
    setSage(null);
    if (selectedCard) hideCard(selectedCard.key);
  };
  const openSage = (action: SageAction) => {
    setAccrualOpen(false);
    setInvoiceSearch(null);
    if (selectedCard) hideCard(selectedCard.key);
    // Re-selecting the open action closes it, so the tile toggles.
    setSage((current) => (current?.key === action.key ? null : action));
  };
  const runAccrual = (query: AccrualQuery) => {
    setInvoiceSearch(null);
    setSage(null);
    if (selectedCard) hideCard(selectedCard.key);
    setAccrualQuery(query);
    setAccrualRun(value => value + 1);
    setAccrualOpen(true);
  };
  const runInvoiceSearch = (params: InvoiceListParams) => {
    if (selectedCard) hideCard(selectedCard.key);
    setAccrualOpen(false);
    setSage(null);
    setInvoiceSearch({ params, run: ++invoiceSearchRun.current });
  };
  const results = invoiceSearch
    ? <InvoiceSearchResults key={invoiceSearch.run} initialParams={invoiceSearch.params} />
    : accrualOpen && accrualQuery
    ? <DriverPayrollAccrualResults key={accrualRun} query={accrualQuery} />
    : sage ? <SageActionResults key={sage.key} action={sage.key} />
    : selectedTarget ? (
      <AccountingDrillCard
        target={selectedTarget}
        refreshTick={refreshTick}
        conditions={bucketFilters[selectedTarget.rowKey] || EMPTY_CONDITIONS}
        onEditFilter={() => setFilterTarget({ key: selectedTarget.rowKey, label: selectedTarget.leafLabel })}
        onClearFilter={() => applyBucketFilter(selectedTarget.rowKey, [])}
        filterSummary={describeConditions(bucketFilters[selectedTarget.rowKey])}
      />
    ) : null;

  // The EDI row under Pending Invoicing is not a cache bucket — its number is the EDI
  // Pending Invoices page's own total across every partner bucket, and the row links
  // there instead of drilling. Fetched alongside the summary and on the same tick.
  const [ediTotal, setEdiTotal] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    getInvoiceBuckets()
      .then((res) => { if (!cancelled) setEdiTotal(res.total ?? 0); })
      // A failed EDI fetch leaves the row at its em dash rather than a false zero; the
      // rest of the dashboard is unaffected.
      .catch(() => { if (!cancelled) setEdiTotal(null); });
    return () => { cancelled = true; };
  }, [refreshTick]);

  const counts = useMemo(
    () => (ediTotal === null ? summary.counts : { ...summary.counts, [EDI_ROW_KEY]: ediTotal }),
    [summary.counts, ediTotal],
  );

  const handleCountSelect = useCallback((rowKey: string) => {
    if (rowKey === EDI_ROW_KEY) {
      // New tab, not a navigation: leaving the dashboard would drop whatever drill the
      // user had open and their scroll position in it.
      window.open(EDI_PAGE_PATH, '_blank', 'noopener,noreferrer');
      return;
    }
    const target = drillTargetFor(rowKey);
    if (!target) return;
    setAccrualOpen(false);
    setInvoiceSearch(null);
    setSage(null);
    selectCard(target.rowKey, target.title);
  }, [selectCard]);

  // ---- Drawer-view preference ---------------------------------------------------------
  // Cosmetic: a failed load just leaves page view. Saves wait for the load so a slow
  // response can never be overwritten by the default.
  const [drawerView, setDrawerView] = useState(false);
  const [viewLoaded, setViewLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    userSettingsService.getUserSetting(ACCT_VIEW_SETTING_KEY)
      .then((setting) => {
        if (cancelled) return;
        const v = setting?.setting_value?.drawerView;
        if (typeof v === 'boolean') setDrawerView(v);
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to load drawer view preference:', err);
      })
      .finally(() => {
        if (!cancelled) setViewLoaded(true);
      });
    return () => { cancelled = true; };
  }, []);
  const setDrawerViewPref = (v: boolean) => {
    // Switching modes clears the open drill so we never strand a full-page view or pop a
    // drawer unexpectedly — same as LC.
    closeResults();
    setDrawerView(v);
    if (viewLoaded) {
      userSettingsService.saveUserSetting(ACCT_VIEW_SETTING_KEY, { drawerView: v })
        .catch((err) => console.error('Failed to save drawer view preference:', err));
    }
  };
  // Page view with a drill open: the tiles hide and the results take the page. Waits for
  // the preference so a persisted drill never flashes as a page swap before the drawer.
  const pageSwap = viewLoaded && !drawerView && hasResults;

  // Resizable drawer — same feel as the PRO detail drawer: drag the inner edge, snap to
  // the app nav within 24px, double-click the handle to toggle snap; width persisted.
  const drawerOpen = drawerView && hasResults;
  const {
    width: drawerWidth,
    onHandleMouseDown: onDrawerHandleMouseDown,
    onHandleDoubleClick: onDrawerHandleDoubleClick,
  } = useDrawerResize({
    isOpen: drawerOpen,
    storageKey: 'acctDrillDrawerWidth',
    minWidth: 480,
    defaultWidth: 0.8, // matches the 80vw the drawer shipped with
  });

  // ---- Layout menu -----------------------------------------------------------------
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const resetDrag = useModalDrag(showResetConfirm);
  const handleResetLayout = () => {
    // Bank the current arrangement so the reset can be undone; resolvePanelItems then
    // falls back to the authored default and applyGeometry pushes it into the grid.
    const reset = trashPanelLayout(
      panelLayoutsRef.current,
      panelTrashRef.current,
      ACCT_WORKSPACE_ID,
      'reset',
      new Date().toISOString(),
    );
    applyTrashResult(reset);
    setShowResetConfirm(false);
    showToast({
      type: 'success',
      message: reset.trashed
        ? 'Layout reset to default — use Restore Panel Layout to undo'
        : 'Layout reset to default',
    });
  };

  const restorableBatch = useMemo(() => latestRestorableBatch(panels.panelTrash), [panels.panelTrash]);
  const handleRestore = () => {
    if (!restorableBatch) return;
    const restored = restorePanelBatch(panelLayoutsRef.current, panelTrashRef.current, restorableBatch.batchId);
    applyTrashResult(restored);
    setMenuOpen(false);
    showToast({ type: 'success', message: 'Panel layout restored' });
  };

  // Copy a colleague's arrangement.
  const [showCopyModal, setShowCopyModal] = useState(false);
  const copyDrag = useModalDrag(showCopyModal);
  const [copySources, setCopySources] = useState<PanelLayoutSource[] | null>(null);
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const [copyQuery, setCopyQuery] = useState('');
  const loadCopySources = useCallback(async () => {
    setCopyLoading(true);
    setCopyError(false);
    try {
      setCopySources(await userSettingsService.getPanelLayoutSources(ACCT_PANELS_SETTING_KEY));
    } catch (err) {
      console.error('Failed to load copyable panel layouts:', err);
      setCopyError(true);
    } finally {
      setCopyLoading(false);
    }
  }, []);
  // Only colleagues who actually arranged their board — copying the default does nothing.
  const copyable = useMemo(
    () => (copySources ?? []).filter((s) => !isDefaultPanelArrangement(s.panel_layouts as PanelLayouts, getDefaultPanelLayouts())),
    [copySources],
  );
  const visibleSources = useMemo(() => {
    const q = copyQuery.trim().toLowerCase();
    if (!q) return copyable;
    return copyable.filter((s) => s.display_name.toLowerCase().includes(q) || s.user_email.toLowerCase().includes(q));
  }, [copyable, copyQuery]);
  const handleCopy = (source: PanelLayoutSource) => {
    const now = new Date().toISOString();
    const copied = applyCopiedPanelLayouts(
      panelLayoutsRef.current,
      panelTrashRef.current,
      source.panel_layouts as PanelLayouts,
      now,
      `copy:${source.user_email}:${now}`,
    );
    applyTrashResult(copied);
    setShowCopyModal(false);
    setMenuOpen(false);
    showToast({ type: 'success', message: `Copied ${source.display_name}'s panel layout — use Restore Panel Layout to undo` });
  };

  // ---- Render ------------------------------------------------------------------------
  const countPanelByKey = useMemo(() => new Map(ACCT_COUNT_PANELS.map((p) => [p.key, p])), []);
  const defByKey = useMemo(() => new Map(ACCT_PANEL_DEFS.map((d) => [d.key, d])), []);
  const lastUpdatedLabel = summary.syncedAt
    ? new Date(summary.syncedAt).toLocaleString([], { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : summary.loading ? '…' : summary.error ? 'unavailable' : 'never';

  return (
    // In page-swap mode the results box must be height-bounded (h-full) so TanTable's
    // virtualizer has a viewport to fill — the same sizing PendingEdiInvoices uses.
    <div className={`p-4 flex flex-col gap-3 ${pageSwap ? 'h-full min-h-0' : 'min-h-full'}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-label">Accounting Dashboards</h1>
          <p className="text-sm text-muted" data-testid="last-updated">Last Updated: {lastUpdatedLabel}</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Inert until the preference loads, so a flip can't be overwritten by a late load. */}
          <Toggle
            variant="with-label"
            size="sm"
            name="acct-drawer-view"
            checked={drawerView}
            onChange={setDrawerViewPref}
            disabled={!viewLoaded}
            ariaLabel="Drawer view"
            label="Drawer view"
          />
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className={`p-1 rounded transition-colors ${menuOpen
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700'}`}
              title="Layout controls"
              aria-label="Layout controls"
            >
              <AdjustmentsHorizontalIcon className="h-4 w-4" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-theme-bg-elevated border border-theme-border-primary rounded-lg shadow-lg z-40">
                <div className="px-4 py-2 border-b border-theme-border-primary">
                  <p className="text-sm font-semibold text-heading">Layout</p>
                </div>
                {/* Layout actions wait for a successful load: before that the state is the
                    code default, so a reset/copy/restore would toast a promise the 15s retry
                    then overwrites. */}
                <div className="p-2">
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); setShowResetConfirm(true); }}
                    className={MENU_ITEM}
                    disabled={!panels.loaded}
                    title={panels.loaded ? undefined : 'Layout actions are unavailable until your saved layout loads'}
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                    Reset Grid Layout
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); setCopyQuery(''); setShowCopyModal(true); loadCopySources(); }}
                    className={MENU_ITEM}
                    disabled={!panels.loaded}
                    title={panels.loaded ? undefined : 'Layout actions are unavailable until your saved layout loads'}
                  >
                    <UserGroupIcon className="h-4 w-4" />
                    Copy Panel Layout From…
                  </button>
                  {restorableBatch && (
                    <button
                      type="button"
                      onClick={handleRestore}
                      className={MENU_ITEM}
                      disabled={!panels.loaded}
                      title={panels.loaded ? undefined : 'Layout actions are unavailable until your saved layout loads'}
                    >
                      <ArrowUturnLeftIcon className="h-4 w-4" />
                      Restore Panel Layout
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tiles. Not rendered until the saved layout has loaded or failed, so the grid
          adopts the right geometry instead of flashing the default and jumping. Stays
          MOUNTED (hidden via CSS) while a drill has the page, so the GridStack instance
          and its geometry survive the swap — same trick as LC. */}
      {panelsReady && (
        <div className={`grid-stack ${pageSwap ? 'hidden' : ''}`} ref={tileGrid.gridRef}>
          {panelItems.map((item) => {
            const def = defByKey.get(item.key);
            if (!def) return null;
            const countConfig = countPanelByKey.get(item.key);
            return (
              <div
                key={item.key}
                className={`grid-stack-item ${item.collapsed ? 'gs-collapsed' : ''}`}
                gs-id={item.key}
                gs-x={item.x}
                gs-y={item.y}
                gs-w={item.w}
                gs-h={item.h}
              >
                <div className="grid-stack-item-content">
                  <DashboardTile
                    title={def.title}
                    collapsed={!!item.collapsed}
                    onToggleCollapse={() => tileGrid.toggleCollapse(item.key)}
                    locked={!!item.locked}
                    onToggleLock={() => tileGrid.toggleLock(item.key)}
                  >
                    {countConfig ? (
                      <CountPanel
                        config={countConfig}
                        counts={counts}
                        amounts={summary.totals}
                        loading={summary.loading && !summary.syncedAt}
                        emptyValue="—"
                        onSelect={handleCountSelect}
                        embedded
                      />
                    ) : item.key === 'acct-total' ? (
                      <TotalPanel
                        count={summary.counts[TOTAL_BUCKET_KEY]}
                        amount={summary.totals[TOTAL_BUCKET_KEY]}
                        loading={summary.loading && !summary.syncedAt}
                      />
                    ) : item.key === 'acct-accrual-driver-payroll' ? (
                      <DriverPayrollAccrualForm onRun={runAccrual} />
                    ) : item.key === 'acct-sage-exports' ? (
                      <SagePanel selected={sage?.key ?? null} onSelect={openSage} />
                    ) : item.key === 'acct-invoice-search' ? (
                      <InvoiceSearchForm onSearch={runInvoiceSearch} />
                    ) : (
                      <ComingSoon panelKey={item.key} />
                    )}
                  </DashboardTile>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Page view: the open drill takes over the page behind a back bar (LC's
          single-card full-screen mode). */}
      {pageSwap && (
        <div key={invoiceSearch ? 'invoice-search' : accrualOpen ? 'accrual' : selectedCard?.key} className="flex-1 min-h-0 flex flex-col">
          <div className="flex-shrink-0 mb-2 flex items-center gap-2">
            <button
              type="button"
              onClick={closeResults}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
              title="Back to dashboard"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to dashboard
            </button>
            <span className="text-gray-300 dark:text-gray-600">/</span>
            <span className="text-sm font-semibold text-body">{resultsTitle}</span>
            {notWired && <NotWiredPill />}
          </div>
          <div className="flex-1 min-h-0 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-600 overflow-hidden flex flex-col">
            {results}
          </div>
        </div>
      )}

      {/* Drawer view: the open drill slides in from the right while the tiles stay
          visible behind. Closing returns to the tiles. */}
      {drawerView && (
        <Drawer
          isOpen={hasResults}
          onClose={closeResults}
          title={(
            <div className="flex items-center gap-3 pr-2">
              <span>{resultsTitle}</span>
              {notWired && <NotWiredPill />}
            </div>
          )}
          position="right"
          width={`${drawerWidth}px`}
          contentClassName="relative flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden p-3"
          // Plain z-[100] portal (not a top-layer <dialog>) so slide-overs opened from
          // inside the results can layer on top, and the tiles stay visible behind.
          noBackdrop
        >
          {/* Resize handle — pinned to the panel's inner (left) edge. Rendered as a
              `fixed` strip tracking the panel edge (right: drawerWidth) instead of an
              absolute child, so it stays put while the results scroll. z-[101] keeps it
              grabbable above the drawer content (drawer panel is z-[100]).
              pointer-events-auto re-enables clicks (the drawer's outer wrapper is
              pointer-events-none). Drag to resize · double-click to snap to nav. */}
          {drawerOpen && (
            <div
              data-testid="acct-drill-resize-handle"
              className="fixed top-0 z-[101] h-full w-1 cursor-ew-resize bg-slate-200 transition-colors hover:bg-blue-400 active:bg-blue-500 pointer-events-auto dark:bg-gray-700 dark:hover:bg-blue-500 max-sm:hidden"
              style={{ right: `${drawerWidth - 4}px` }}
              onMouseDown={onDrawerHandleMouseDown}
              onDoubleClick={onDrawerHandleDoubleClick}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize panel"
              title="Drag to resize · double-click to snap to nav"
            />
          )}
          {hasResults && (
            <div className="flex-1 min-h-0 min-w-0 overflow-hidden flex flex-col bg-gray-50 dark:bg-gray-800 rounded-lg">
              {results}
            </div>
          )}
        </Drawer>
      )}

      {/* Reset confirmation */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-scrim bg-opacity-50 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div ref={resetDrag.panelRef} style={resetDrag.panelStyle} className="bg-panel rounded-lg shadow-xl max-w-md w-full">
            <ModalHeader title="Reset Grid Layout" onClose={() => setShowResetConfirm(false)} dragHandleProps={resetDrag.dragHandleProps} />
            <div className="p-6">
              <p className="text-sm text-label">
                Put every tile back in its default position and size? Your current arrangement is kept —
                use <span className="font-medium">Restore Panel Layout</span> in the same menu to undo.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleResetLayout}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
              >
                Reset Layout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy a colleague's layout */}
      {showCopyModal && (
        <div className="fixed inset-0 bg-scrim bg-opacity-50 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div ref={copyDrag.panelRef} style={copyDrag.panelStyle} className="bg-panel rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
            <ModalHeader title="Copy Panel Layout From" onClose={() => setShowCopyModal(false)} dragHandleProps={copyDrag.dragHandleProps} />
            <div className="p-6 pb-3">
              <p className="text-sm text-muted">
                Replaces your tile arrangement with theirs. Yours is kept —{' '}
                <span className="font-medium">Restore Panel Layout</span> puts it back. Your drill cards and table settings are not affected.
              </p>
              {copyable.length > 0 && (
                <input
                  type="text"
                  value={copyQuery}
                  onChange={(e) => setCopyQuery(e.target.value)}
                  placeholder="Filter by name…"
                  className="mt-3 w-full px-3 py-2 text-sm rounded-md bg-theme-bg-secondary border border-theme-border-primary text-label placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            </div>
            <div className="flex-1 overflow-y-auto px-6 pb-6">
              {copyLoading && <p className="py-6 text-sm text-muted text-center">Loading layouts…</p>}
              {!copyLoading && copyError && (
                <div className="py-6 text-center">
                  <p className="text-sm text-label">Couldn't load the list of layouts.</p>
                  <button
                    type="button"
                    onClick={loadCopySources}
                    className="mt-2 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                  >
                    Try again
                  </button>
                </div>
              )}
              {!copyLoading && !copyError && copyable.length === 0 && (
                <p className="py-6 text-sm text-muted text-center">Nobody else has arranged their tiles yet.</p>
              )}
              {!copyLoading && !copyError && copyable.length > 0 && visibleSources.length === 0 && (
                <p className="py-6 text-sm text-muted text-center">No one matching "{copyQuery.trim()}".</p>
              )}
              {!copyLoading && !copyError && visibleSources.length > 0 && (
                <div className="space-y-2">
                  {visibleSources.map((source) => (
                    <div key={source.user_email} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-theme-border-primary">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-heading truncate">{source.display_name}</p>
                        <p className="text-xs text-muted truncate">
                          {source.panel_count} tile{source.panel_count === 1 ? '' : 's'}
                          {source.updated_at ? ` · updated ${new Date(source.updated_at).toLocaleDateString()}` : ''}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleCopy(source)}
                        className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                      >
                        Use this layout
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* The bucket's custom filter. Same dialog the LC board uses, so a saved filter is
          the same saved filter in both places — one pool, reachable wherever orders are
          filtered. */}
      {filterTarget && (
        <BucketFilterModal
          isOpen
          bucketLabel={filterTarget.label}
          initialConditions={bucketFilters[filterTarget.key]}
          onApply={(conditions) => applyBucketFilter(filterTarget.key, conditions)}
          onClose={() => setFilterTarget(null)}
        />
      )}
    </div>
  );
};

export default AccountingDashboards;
